import { RouteProp, useRoute } from '@react-navigation/native';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageBackground, StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import { LeftArrowPng, RightArrowPng } from '../../../assets';
import { RootState } from '../../../store';
import { tournamentsActiveRequest } from '../../../store/tournaments/tournamentsActions';
import { TableItem } from '../../../store/types';
import { useDevice } from '../../../utils/hooks/useDevice';
import { RootStackParamList } from '../../navigator';
import Carousel from '../common/PlatformCarousel';
import { TouchableOpacity } from '../common/Touchable';
import TournamentTable from '../LobbyTable/TournamentTable';

export type ActiveTournamentRoute = RouteProp<RootStackParamList, 'ActiveTournament'>;

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

export const ActiveTournamentsSection = () => {
	const dispatch = useDispatch();
	const { params } = useRoute<ActiveTournamentRoute>();
	const { t } = useTranslation();

	const { safeAreaWidth, safeAreaHeight, insets, isSm, isXs, isDesktop } = useDevice();

	const topReserve = safeAreaHeight * (isDesktop ? 0.13 : 0.1) + insets.top;
	const bottomReserve = isDesktop
		? safeAreaHeight * 0.2 + insets.bottom
		: (isXs ? safeAreaWidth * 0.2 : safeAreaHeight * 0.18) + insets.bottom;
	const middleHeight = Math.max(
		safeAreaHeight - topReserve - bottomReserve,
		safeAreaHeight * (isDesktop ? 0.36 : 0.52),
	);
	const sectionHeight = Math.round(
		clamp(middleHeight, isDesktop ? 240 : 440, safeAreaHeight * (isDesktop ? 0.7 : 0.78)),
	);

	const itemsPerPage = isDesktop ? 3 : 1;
	const sectionTopOffset = isSm ? 0 : 40;
	const pageGap = isDesktop ? 24 : 0;
	const sideInset = isDesktop ? safeAreaWidth * 0.12 : safeAreaWidth * 0.08;
	const pageInnerWidth = isDesktop ? safeAreaWidth - sideInset * 2 : safeAreaWidth;
	const cardWidth = Math.round(
		isDesktop
			? (pageInnerWidth - pageGap * Math.max(0, itemsPerPage - 1)) / itemsPerPage
			: safeAreaWidth * 0.84,
	);

	const carouselRef = useRef<any>(null);

	const roomId = String(params?.roomId ?? '');
	const roomName = params?.roomName ?? '';

	const activeState = useSelector((state: RootState) => state.tournaments.activeByRoom?.[roomId]);

	const tournaments = useMemo(() => activeState?.data ?? [], [activeState?.data]);
	const loading = activeState?.loading ?? false;
	const error = activeState?.error ?? null;

	useEffect(() => {
		if (roomId) {
			dispatch(tournamentsActiveRequest({ roomId }));
		}
	}, [dispatch, roomId]);

	const pages: TableItem[][] = useMemo(() => {
		const out: TableItem[][] = [];
		for (let i = 0; i < tournaments.length; i += itemsPerPage) {
			out.push(tournaments.slice(i, i + itemsPerPage));
		}
		return out;
	}, [tournaments, itemsPerPage]);

	const getPageContentWidth = (pageLength: number) => {
		if (!isDesktop || pageLength <= 1) return cardWidth;
		return Math.min(pageInnerWidth, cardWidth * pageLength + pageGap * (pageLength - 1));
	};

	const handleNext = () => {
		carouselRef.current?.next();
	};

	const handlePrev = () => {
		carouselRef.current?.prev();
	};

	return (
		<View
			style={[
				styles.container,
				{
					marginTop: sectionTopOffset,
					justifyContent: 'center',
					width: safeAreaWidth,
					margin: 'auto',
					height: sectionHeight,
				},
			]}
		>
			{loading ? (
				<Text style={styles.message}>{t('T_SITE_TOURNAMENT_LOADING')}</Text>
			) : error ? (
				<Text style={styles.message}>{error}</Text>
			) : pages.length === 0 ? (
				<Text style={styles.message}>{t('T_SITE_TOURNAMENT_NO_ACTIVE_TABLES')}</Text>
			) : (
				<>
					<View
						style={{
							justifyContent: 'center',
							marginTop: sectionTopOffset,
							height: sectionHeight,
							overflow: 'visible',
						}}
					>
						<Carousel
							ref={carouselRef}
							loop={pages.length > 1}
							width={safeAreaWidth}
							height={sectionHeight}
							autoPlay={false}
							data={pages}
							scrollAnimationDuration={1200}
							renderItem={({ item: page, index: pageIndex }) => (
								<View
									style={{
										flexDirection: 'row',
										justifyContent: !isDesktop || page.length === 1 ? 'center' : 'space-between',
										alignItems: 'center',
										width: getPageContentWidth(page.length),
										height: sectionHeight,
										paddingHorizontal: isDesktop ? 8 : 0,
										marginHorizontal: 'auto',
										overflow: 'visible',
									}}
								>
									{page.map((room, itemIndex) => {
										const globalIndex = pageIndex * itemsPerPage + itemIndex;

										return (
											<View
												key={room.id ?? globalIndex}
												style={{
													width: cardWidth,
													height: sectionHeight,
													marginRight: itemIndex < page.length - 1 ? pageGap : 0,
													alignItems: 'center',
													justifyContent: 'center',
													overflow: 'visible',
												}}
											>
												<TournamentTable
													key={room.id ?? itemIndex}
													room={room}
													totalCount={tournaments.length}
													itemIndex={globalIndex}
													roomName={roomName}
												/>
											</View>
										);
									})}
								</View>
							)}
						/>
					</View>

					{pages.length > 1 && (
						<>
							<TouchableOpacity style={styles.arrowLeft} onPress={handlePrev}>
								<ImageBackground
									source={LeftArrowPng}
									resizeMode='cover'
									style={styles.arrowBg}
									imageStyle={styles.arrowImage}
								/>
							</TouchableOpacity>

							<TouchableOpacity style={styles.arrowRight} onPress={handleNext}>
								<ImageBackground
									source={RightArrowPng}
									resizeMode='cover'
									style={styles.arrowBg}
									imageStyle={styles.arrowImage}
								/>
							</TouchableOpacity>
						</>
					)}
				</>
			)}
		</View>
	);
};

export default ActiveTournamentsSection;

const styles = StyleSheet.create({
	container: {
		position: 'relative',
		justifyContent: 'center',
		alignItems: 'center',
		backgroundColor: 'transparent',
	},
	arrowLeft: {
		position: 'absolute',
		top: '45%',
		left: 10,
		borderRadius: 20,
		zIndex: 10,
	},
	arrowRight: {
		position: 'absolute',
		top: '45%',
		right: 10,
		borderRadius: 20,
		zIndex: 10,
	},
	arrowBg: {
		width: 50,
		height: 50,
		justifyContent: 'center',
		alignItems: 'center',
		borderRadius: 20,
		padding: 10,
	},
	arrowImage: {
		borderRadius: 20,
	},
	message: {
		color: '#fff',
		fontSize: 20,
		textAlign: 'center',
	},
});
